<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import fromUnixTime from 'date-fns/fromUnixTime';
import { formatTime } from '@chatwoot/utils';
import BarChart from 'shared/components/charts/BarChart.vue';
import { useLocale } from 'shared/composables/useLocale';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import {
  resolveTableColumns,
  isCustomAttributeColumn,
  isContactCustomAttributeColumn,
  customAttributeKeyFromColumn,
  measureOpFromColumn,
  isPivotColumnKey,
  parsePivotColumnKey,
  parseLocaleNumber,
  formatNumericAttribute,
  formatPanelMeasure,
  SUMMABLE_CUSTOM_TYPES,
  TABLE_MEASURE_LABELS,
  PIVOT_IDENTITY_COLUMNS,
  PANEL_DATE_FORMATS,
  formatPanelDate,
} from '../panelConstants';

const props = defineProps({
  widget: { type: Object, required: true },
  result: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  attributeLabels: { type: Object, default: () => ({}) },
  attributeTypes: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { resolvedLocale: locale } = useLocale();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const numberFormatter = computed(
  () => new Intl.NumberFormat(locale.value, { useGrouping: true })
);
const formatNumber = value => numberFormatter.value.format(Number(value));
const errorMessage = computed(() => {
  const error = props.error || props.result?.error;
  if (!error) return '';
  if (
    error.startsWith('pivot_row_limit_exceeded') ||
    error.startsWith('pivot_bucket_limit_exceeded')
  ) {
    return t('REPORT_PANELS.DYNAMIC.LIMIT_ERROR');
  }
  return props.error || t('REPORT_PANELS.ERRORS.RUN');
});
const isLoading = computed(
  () => props.loading || (!props.result && !errorMessage.value)
);

const TIME_COLUMNS = new Set([
  'avg_first_response_time',
  'avg_resolution_time',
  'avg_reply_time',
  'reply_time',
]);

const DATE_COLUMNS = new Set(['created_at', 'last_activity_at']);

const sortKey = ref(null);
const sortDir = ref('asc'); // 'asc' | 'desc'

watch(
  () => props.result?.rows,
  () => {
    sortKey.value = null;
    sortDir.value = 'asc';
  }
);

const isTimeMetric = metric =>
  ['avg_first_response_time', 'avg_resolution_time', 'reply_time'].includes(
    metric
  );

const isAggregationSource = computed(
  () =>
    props.widget.source === 'aggregation' ||
    props.result?.source === 'aggregation'
);

const attributeTypes = computed(() => ({
  ...(props.attributeTypes || {}),
  ...(props.result?.attribute_types || {}),
}));

const aggregationFieldType = computed(() => {
  if (!isAggregationSource.value) return null;
  const field =
    props.widget.aggregation_field || props.result?.aggregation_field;
  if (!field) return null;
  const attrKey = customAttributeKeyFromColumn(field) || field;
  return (
    attributeTypes.value[field] ||
    attributeTypes.value[attrKey] ||
    attributeTypes.value[`ca:${attrKey}`] ||
    null
  );
});

const displayValue = computed(() => {
  if (!props.result || props.result.error) return '--';
  if (props.widget.type !== 'metric') return '';
  const value = props.result.value;
  if (value == null) return '--';
  if (!isAggregationSource.value && isTimeMetric(props.widget.metric)) {
    return formatTime(value);
  }
  const type = aggregationFieldType.value;
  if (type && SUMMABLE_CUSTOM_TYPES.has(type)) {
    return formatNumericAttribute(value, type, locale.value);
  }
  return formatNumber(value);
});

const metricLabel = computed(() => {
  if (isAggregationSource.value) {
    const op = props.widget.aggregation_op || props.result?.aggregation_op;
    const field =
      props.widget.aggregation_field || props.result?.aggregation_field;
    const opLabel = op
      ? t(`REPORT_PANELS.AGGREGATIONS.${String(op).toUpperCase()}`)
      : '';
    if (!field) return opLabel;
    const attrKey = customAttributeKeyFromColumn(field) || field;
    const fieldLabel = props.attributeLabels[attrKey] || attrKey;
    return `${opLabel}: ${fieldLabel}`;
  }
  const metric = props.widget.metric;
  if (!metric) return '';
  return t(`REPORT_PANELS.METRICS.${metric}`);
});

const chartData = computed(() => {
  const points = props.result?.points || [];
  const groupBy =
    props.widget.group_by in PANEL_DATE_FORMATS ? props.widget.group_by : 'day';
  const timeMetric =
    !isAggregationSource.value && isTimeMetric(props.widget.metric);
  return {
    categories: points.map(point =>
      formatPanelDate(
        fromUnixTime(point.timestamp),
        locale.value,
        PANEL_DATE_FORMATS[groupBy]
      )
    ),
    series: [
      {
        id: props.widget.id || 'panel-chart',
        // Series label = real metric (not custom title) so tooltips match data.
        label: timeMetric
          ? `${metricLabel.value} (${t('REPORT_PANELS.CHART_TIME_UNIT')})`
          : metricLabel.value || props.widget.title || '',
        color: 'rgb(var(--blue-9))',
        data: points.map(point => {
          const value = Number(point.value) || 0;
          // Time metrics arrive as seconds, so plot hours for a readable scale.
          return {
            ...point,
            value: timeMetric ? Math.round((value / 3600) * 10) / 10 : value,
          };
        }),
      },
    ],
  };
});

const tableKind = computed(
  () => props.widget.table_kind || props.result?.table_kind || 'agent_summary'
);

const tableRows = computed(() => props.result?.rows || []);

const tableHeaders = computed(() => {
  // Pivot runner returns expanded columns (measure__pv__value).
  if (props.result?.columns?.length) {
    return props.result.columns;
  }
  const configured = resolveTableColumns(tableKind.value, props.widget.columns);
  if (configured.length) {
    if (!tableRows.value.length) return configured;
    return configured.filter(
      key =>
        Object.prototype.hasOwnProperty.call(tableRows.value[0], key) ||
        isCustomAttributeColumn(key)
    );
  }
  return tableRows.value.length ? Object.keys(tableRows.value[0]) : [];
});

const columnType = key => {
  if (!isCustomAttributeColumn(key)) return null;
  const attrKey = customAttributeKeyFromColumn(key);
  return attributeTypes.value[key] || attributeTypes.value[attrKey] || null;
};

const NUMERIC_SYSTEM_COLUMNS = new Set([
  'id',
  'rank',
  'conversations_count',
  'resolved_conversations_count',
  'unique_contacts_count',
  'contacts_count',
  'resolutions_count',
  'incoming_messages_count',
  'outgoing_messages_count',
  'share_percent',
  ...TIME_COLUMNS,
]);

/** Currency / number / percent / counts / times — right-aligned. */
const isNumericColumn = key => {
  if (NUMERIC_SYSTEM_COLUMNS.has(key)) return true;
  if (isPivotColumnKey(key)) {
    const measure = parsePivotColumnKey(key)?.measure;
    return measure ? isNumericColumn(measure) : true;
  }
  if (measureOpFromColumn(key)) return true;
  const type = columnType(key);
  return Boolean(type && SUMMABLE_CUSTOM_TYPES.has(type));
};

const headerCellClass = key =>
  [
    'py-2 px-2 text-n-slate-11 font-medium min-w-0',
    isNumericColumn(key) ? 'text-end' : 'text-start',
    key === tableHeaders.value[0] ? 'sticky start-0 z-20 bg-n-solid-2' : '',
  ].join(' ');

const bodyCellClass = (key, footer = false) =>
  [
    'py-2 px-2 text-n-slate-12',
    isNumericColumn(key)
      ? 'text-end whitespace-nowrap'
      : 'text-start max-w-[14rem] min-w-0',
    key === tableHeaders.value[0]
      ? `sticky start-0 z-10 ${footer ? 'bg-n-solid-1' : 'bg-n-solid-2'}`
      : '',
  ].join(' ');

const footerCellClass = key =>
  `${bodyCellClass(key, true)} font-medium bg-n-solid-1`;

const hasCrossedColumns = computed(() =>
  tableHeaders.value.some(isPivotColumnKey)
);

// Group adjacent columns only, so headers, cells and sorting keep the API order.
const headerGroups = computed(() => {
  const groups = [];
  tableHeaders.value.forEach(key => {
    const parsed = parsePivotColumnKey(key);
    const isTotal =
      hasCrossedColumns.value && !parsed && !PIVOT_IDENTITY_COLUMNS.has(key);
    let groupKey = key;
    if (parsed) groupKey = `pivot:${parsed.encoded}`;
    else if (isTotal) groupKey = 'totals';
    const isGroup = Boolean(parsed || isTotal);
    const previous = groups[groups.length - 1];
    if (isGroup && previous?.groupKey === groupKey) {
      previous.columns.push(key);
    } else {
      groups.push({
        key,
        groupKey,
        isGroup,
        isTotal,
        segment: parsed?.value,
        columns: [key],
      });
    }
  });
  return groups;
});
const measureHeaders = computed(() =>
  headerGroups.value
    .filter(group => group.isGroup)
    .flatMap(group => group.columns)
);
const headerRows = computed(() => [
  headerGroups.value,
  ...(hasCrossedColumns.value
    ? [measureHeaders.value.map(key => ({ key, columns: [key] }))]
    : []),
]);

const sortValueFor = (row, key) => {
  const value = row?.[key];
  if (value == null || value === '') return null;

  if (measureOpFromColumn(key) === 'count') {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }

  const type = columnType(key);
  if (type && SUMMABLE_CUSTOM_TYPES.has(type)) {
    return parseLocaleNumber(value);
  }
  if (
    TIME_COLUMNS.has(key) ||
    DATE_COLUMNS.has(key) ||
    key === 'share_percent'
  ) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  if (typeof value === 'number') return value;

  const asNum = parseLocaleNumber(value);
  if (asNum != null && /^-?[\d,.\s$%]+$/.test(String(value).trim())) {
    return asNum;
  }
  return String(value).toLocaleLowerCase();
};

const sortedTableRows = computed(() => {
  const rows = tableRows.value;
  if (!sortKey.value || !rows.length) return rows;

  const key = sortKey.value;
  const dir = sortDir.value === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (a.is_others || b.is_others)
      return Number(Boolean(a.is_others)) - Number(Boolean(b.is_others));
    const av = sortValueFor(a, key);
    const bv = sortValueFor(b, key);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return (
      String(av).localeCompare(String(bv), locale.value, {
        numeric: true,
        sensitivity: 'base',
      }) * dir
    );
  });
});

const toggleSort = key => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    return;
  }
  sortKey.value = key;
  sortDir.value = 'asc';
};

const sortIconClass = key => {
  if (sortKey.value !== key) return 'i-lucide-arrow-up-down text-n-slate-9';
  return sortDir.value === 'desc'
    ? 'i-lucide-arrow-down text-n-brand'
    : 'i-lucide-arrow-up text-n-brand';
};

const totals = computed(() => {
  const fromBackend = props.result?.totals;
  if (fromBackend && typeof fromBackend === 'object') {
    return {
      count:
        fromBackend.count ??
        props.result?.total_count ??
        tableRows.value.length,
      columns: fromBackend.columns || {},
      ops: fromBackend.ops || {},
    };
  }
  return {
    count: props.result?.total_count ?? tableRows.value.length,
    columns: {},
    ops: {},
  };
});

const hasFooterAggregations = computed(
  () => Object.keys(totals.value.columns || {}).length > 0
);

const rowsClickable = computed(() =>
  ['conversations', 'contacts'].includes(tableKind.value)
);

const headerLabelForMeasure = (key, short = false) => {
  if (isCustomAttributeColumn(key)) {
    const attrKey = customAttributeKeyFromColumn(key);
    const op = measureOpFromColumn(key);
    const name =
      props.attributeLabels[key] ||
      props.attributeLabels[
        isContactCustomAttributeColumn(key)
          ? `contact_ca:${attrKey}`
          : `ca:${attrKey}`
      ] ||
      props.attributeLabels[attrKey] ||
      attrKey;
    if (!op) return name;
    const opLabel = t(`REPORT_PANELS.AGGREGATIONS.${String(op).toUpperCase()}`);
    if (short) {
      return t('REPORT_PANELS.COLUMNS.MEASURE_CA_SHORT', {
        name,
        op: opLabel.toLocaleLowerCase(locale.value),
      });
    }
    return t('REPORT_PANELS.COLUMNS.MEASURE_CA', { op: opLabel, name });
  }
  const labels = TABLE_MEASURE_LABELS[key];
  if (labels) return t((short && labels.shortLabelKey) || labels.labelKey);
  const i18nKey = `REPORT_PANELS.COLUMNS.${key}`;
  const translated = t(i18nKey);
  return translated === i18nKey ? key : translated;
};

const headerLabel = (key, short = false) => {
  const dimension = props.widget.pivot?.row_attribute;
  if (key === 'name' && dimension) {
    if (isCustomAttributeColumn(dimension))
      return (
        props.attributeLabels[dimension] ||
        customAttributeKeyFromColumn(dimension)
      );
    return t(`REPORT_PANELS.DYNAMIC.ROWS_${dimension.toUpperCase()}`);
  }
  if (isPivotColumnKey(key)) {
    const parsed = parsePivotColumnKey(key);
    if (short) return headerLabelForMeasure(parsed.measure, true);
    const measureLabel = headerLabelForMeasure(parsed.measure);
    const segment = parsed.value || t('REPORT_PANELS.PIVOT.BLANK_VALUE');
    return t('REPORT_PANELS.PIVOT.HEADER', {
      segment,
      measure: measureLabel,
    });
  }
  return headerLabelForMeasure(key, short);
};

const formatCustomAttributeCell = (value, type) => {
  if (value == null || value === '') return '—';
  switch (type) {
    case 'checkbox':
      return value === true || value === 'true'
        ? t('FILTER.ATTRIBUTE_LABELS.TRUE')
        : t('FILTER.ATTRIBUTE_LABELS.FALSE');
    case 'date': {
      return formatPanelDate(value, locale.value);
    }
    case 'datetime': {
      return formatPanelDate(value, locale.value, {
        ...PANEL_DATE_FORMATS.day,
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    case 'number':
    case 'currency':
    case 'percent':
      return formatNumericAttribute(value, type, locale.value);
    case 'list':
      return Array.isArray(value) ? value.join(', ') : String(value);
    default:
      return String(value);
  }
};

const segmentLabel = group => {
  if (group.isTotal) return t('REPORT_PANELS.TOTALS.FOOTER_LABEL');
  if (group.segment === '') return t('REPORT_PANELS.PIVOT.BLANK_VALUE');
  return formatCustomAttributeCell(
    group.segment,
    columnType(props.widget.pivot?.column_attribute)
  );
};

const measureFormat = key => {
  const base = parsePivotColumnKey(key)?.measure || key;
  const explicit = props.widget.column_formats?.[base];
  if (explicit) return explicit;
  const display = props.widget.measure_display?.[base];
  return display && display !== 'value'
    ? { type: 'percent', decimals: 2 }
    : null;
};
const formatCell = (row, key) => {
  const value = row[key];
  if (key === 'name' && row.is_others) return t('REPORT_PANELS.DYNAMIC.OTHERS');
  if (key === 'name' && value === '' && props.widget.pivot?.row_attribute)
    return t('REPORT_PANELS.DYNAMIC.BLANK');
  if (key === 'name' && props.widget.pivot?.row_attribute === 'created_at') {
    return formatPanelDate(
      value,
      locale.value,
      PANEL_DATE_FORMATS[props.widget.pivot.row_granularity || 'day']
    );
  }
  if (
    key === 'name' &&
    isCustomAttributeColumn(props.widget.pivot?.row_attribute)
  ) {
    return formatCustomAttributeCell(
      value,
      columnType(props.widget.pivot.row_attribute)
    );
  }
  const configuredFormat = measureFormat(key);
  if (configuredFormat)
    return formatPanelMeasure(value, configuredFormat, locale.value);
  const measureKey = isPivotColumnKey(key)
    ? parsePivotColumnKey(key)?.measure
    : key;
  if (isCustomAttributeColumn(measureKey) || isPivotColumnKey(key)) {
    // Summary / pivot measure columns are already aggregated numbers.
    if (measureOpFromColumn(measureKey) || isPivotColumnKey(key)) {
      if (value == null || value === '') return '—';
      if (
        measureOpFromColumn(measureKey) === 'count' ||
        measureKey === 'conversations_count' ||
        measureKey === 'resolved_conversations_count'
      ) {
        return formatNumber(value);
      }
      const type = columnType(measureKey);
      if (type && SUMMABLE_CUSTOM_TYPES.has(type)) {
        return formatNumericAttribute(value, type, locale.value);
      }
      return formatNumber(value);
    }
    if (isCustomAttributeColumn(measureKey)) {
      return formatCustomAttributeCell(value, columnType(measureKey));
    }
  }
  if (value == null || value === '') return '—';
  if (TIME_COLUMNS.has(key)) return formatTime(value);
  if (key === 'share_percent')
    return formatPanelMeasure(
      value,
      { type: 'percent', decimals: 1 },
      locale.value
    );
  if (DATE_COLUMNS.has(key) && typeof value === 'number') {
    return formatPanelDate(fromUnixTime(value), locale.value, {
      ...PANEL_DATE_FORMATS.day,
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (key === 'status') {
    const statusKey = `CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${value}.TEXT`;
    const translated = t(statusKey);
    return translated === statusKey ? value : translated;
  }
  if (key === 'priority' && value) {
    const priorityKey = `CONVERSATION.PRIORITY.OPTIONS.${String(value).toUpperCase()}`;
    const translated = t(priorityKey);
    return translated === priorityKey ? value : translated;
  }
  if (key === 'labels') {
    return Array.isArray(value) ? value.join(', ') : String(value);
  }
  if (
    typeof value === 'number' &&
    !TIME_COLUMNS.has(key) &&
    !DATE_COLUMNS.has(key) &&
    key !== 'id' &&
    key !== 'rank'
  ) {
    return formatNumber(value);
  }
  return value;
};

const cellTitle = (row, key) => {
  if (isNumericColumn(key)) return undefined;
  const formatted = formatCell(row, key);
  if (formatted == null || formatted === '' || formatted === '—') {
    return undefined;
  }
  return String(formatted);
};

const formatAggregateValue = (key, value) => {
  const configuredFormat = measureFormat(key);
  if (configuredFormat)
    return formatPanelMeasure(value, configuredFormat, locale.value);
  if (value == null || value === '') return '—';
  if (TIME_COLUMNS.has(key)) return formatTime(value);
  if (key === 'share_percent')
    return formatPanelMeasure(
      value,
      { type: 'percent', decimals: 1 },
      locale.value
    );
  if (measureOpFromColumn(key) === 'count') {
    return formatNumber(value);
  }
  const type = columnType(key);
  if (type && SUMMABLE_CUSTOM_TYPES.has(type)) {
    return formatNumericAttribute(value, type, locale.value);
  }
  return formatNumber(value);
};

const footerCell = key => {
  const value = totals.value.columns?.[key];
  if (value == null) return '';
  const op = totals.value.ops?.[key];
  const formatted = formatAggregateValue(key, value);
  if (!op) return formatted;
  const opLabel = t(`REPORT_PANELS.AGGREGATIONS.${String(op).toUpperCase()}`);
  return `${opLabel}: ${formatted}`;
};

const openRow = row => {
  if (!rowsClickable.value || row?.id == null) return;
  if (tableKind.value === 'conversations') {
    router.push(
      accountScopedRoute('inbox_conversation', {
        conversation_id: row.id,
      })
    );
    return;
  }
  if (tableKind.value === 'contacts') {
    router.push(
      accountScopedRoute('contacts_edit', {
        contactId: row.id,
      })
    );
  }
};

const title = computed(() => {
  if (props.widget.title) return props.widget.title;
  if (props.widget.type === 'table') {
    if (props.widget.pivot?.row_attribute) return headerLabel('name');
    const kind = tableKind.value;
    const keyMap = {
      conversations: 'CONVERSATIONS',
      contacts: 'CONTACTS',
    };
    return t(`REPORT_PANELS.TABLE_KINDS.${keyMap[kind]}`);
  }
  return metricLabel.value;
});

const tableSubtitle = computed(() => {
  if (props.widget.type !== 'table' || isLoading.value || errorMessage.value)
    return '';
  const total = totals.value.count;
  if (total == null) return '';
  const shown = tableRows.value.length;
  if (props.result?.truncated && shown < total) {
    return t('REPORT_PANELS.TOTALS.TRUNCATED_COUNT', {
      shown: formatNumber(shown),
      total: formatNumber(total),
    });
  }
  return t('REPORT_PANELS.TOTALS.RECORDS_COUNT', {
    count: formatNumber(total),
  });
});
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-3 min-h-[10rem] min-w-0 max-w-full"
    :aria-busy="isLoading"
  >
    <div class="flex flex-col gap-0.5">
      <h3 class="text-sm font-medium text-n-slate-12 break-words">
        {{ title }}
      </h3>
      <p v-if="tableSubtitle" class="text-xs text-n-slate-11">
        {{ tableSubtitle }}
      </p>
      <p
        v-else-if="widget.type !== 'table' && metricLabel"
        class="text-xs text-n-slate-11"
      >
        {{ metricLabel }}
      </p>
    </div>

    <div
      v-if="isLoading"
      role="status"
      class="flex flex-1 items-center justify-center gap-2 py-4 text-sm text-n-slate-11"
    >
      <Spinner :size="20" />
      <span>{{ t('REPORT_PANELS.LOADING') }}</span>
    </div>
    <p
      v-else-if="errorMessage"
      role="alert"
      class="text-sm text-n-ruby-11 break-words py-4"
    >
      {{ errorMessage }}
    </p>

    <div
      v-else-if="widget.type === 'metric'"
      class="text-3xl font-semibold text-n-slate-12"
    >
      {{ displayValue }}
    </div>

    <div v-else-if="widget.type === 'chart'">
      <BarChart
        :data="chartData"
        :height="224"
        timeseries
        :format-value="formatNumber"
        :aria-label="widget.title || metricLabel"
      />
    </div>

    <div
      v-else-if="widget.type === 'table'"
      class="flex flex-col gap-3 min-w-0 max-w-full"
    >
      <p
        v-if="!tableRows.length"
        role="status"
        class="text-sm text-n-slate-11 py-4"
      >
        {{ t('REPORT_PANELS.EMPTY_TABLE') }}
      </p>
      <div v-else class="overflow-x-auto min-w-0 max-w-full">
        <table class="w-full text-sm">
          <thead>
            <tr v-for="(headers, level) in headerRows" :key="level">
              <th
                v-for="header in headers"
                :key="header.key"
                :class="headerCellClass(header.key)"
                :scope="header.isGroup ? 'colgroup' : 'col'"
                :colspan="header.columns.length"
                :rowspan="
                  hasCrossedColumns && level === 0 && !header.isGroup ? 2 : 1
                "
                :aria-sort="
                  header.isGroup
                    ? undefined
                    : sortKey === header.key
                      ? sortDir === 'desc'
                        ? 'descending'
                        : 'ascending'
                      : 'none'
                "
              >
                <span
                  v-if="header.isGroup"
                  class="block truncate max-w-[14rem] text-start"
                  :title="segmentLabel(header)"
                >
                  {{ segmentLabel(header) }}
                </span>
                <button
                  v-else
                  type="button"
                  class="flex items-center gap-1.5 w-full max-w-[12rem] min-w-0 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand rounded-sm"
                  :class="
                    isNumericColumn(header.key)
                      ? 'justify-end text-end'
                      : 'text-start'
                  "
                  :title="headerLabel(header.key)"
                  @click="toggleSort(header.key)"
                >
                  <span class="truncate min-w-0">
                    {{ headerLabel(header.key, true) }}
                  </span>
                  <span
                    class="size-3.5 shrink-0"
                    :class="sortIconClass(header.key)"
                    aria-hidden="true"
                  />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in sortedTableRows"
              :key="index"
              class="border-t border-n-weak"
              :class="{
                'cursor-pointer hover:bg-n-alpha-black2': rowsClickable,
              }"
              @click="openRow(row)"
            >
              <td
                v-for="header in tableHeaders"
                :key="header"
                :class="bodyCellClass(header)"
              >
                <span
                  :class="[
                    isNumericColumn(header)
                      ? ''
                      : 'block truncate max-w-[12rem]',
                    rowsClickable && header === tableHeaders[0]
                      ? 'text-n-brand font-medium'
                      : '',
                  ]"
                  :title="cellTitle(row, header)"
                >
                  {{ formatCell(row, header) }}
                </span>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="sortedTableRows.length && hasFooterAggregations">
            <tr class="border-t-2 border-n-weak bg-n-solid-1">
              <td
                v-for="(header, index) in tableHeaders"
                :key="`footer-${header}`"
                :class="footerCellClass(header)"
              >
                <template v-if="index === 0">
                  <span>{{ t('REPORT_PANELS.TOTALS.FOOTER_LABEL') }}</span>
                  <span v-if="footerCell(header)">
                    {{ ` · ${footerCell(header)}` }}
                  </span>
                </template>
                <template v-else>
                  {{ footerCell(header) }}
                </template>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p
        v-if="tableRows.length && rowsClickable"
        class="text-xs text-n-slate-11"
      >
        {{ t('REPORT_PANELS.TABLE_CLICK_HINT') }}
      </p>
    </div>
  </div>
</template>
